import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy
} from "@angular/core";
import { ModalController, PopoverController } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { PopoverAnagraphActionsComponent } from "src/components/popup/popover-anagraph-actions/popover-anagraph-actions.component";
import { PopupSelectOperationComponent } from "src/components/popup/popup-select-operation/popup-select-operation.component";
import { environment } from "src/environments/environment";
import { AccountingRequestFile } from "src/model/class/general classes/accounting-entities";
import { AccountingNote } from "src/model/class/general classes/accounting-note";
import { Column, TableInfo } from "src/components/shared-components/class/general-classes/item-history";
import { StaticClass } from "src/components/shared-components/class/general-classes/static-classes";
import { ApiErrorType } from "src/model/enum/enumApiErrorType";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { ConnectionType } from "src/components/shared-components/enum/enumSettings";
import { ColumnTableType, HideShowColumn } from "src/components/shared-components/enum/enumTableHistory";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { iGenericTable } from "src/model/interface/iGeneric-table";
import { AccountingService } from "src/service/accounting.service";
import { AlertService } from "src/service/alert.service";
import { EventService } from "src/service/event.service";
import { LoggerService } from "src/service/logger.service";
import { MediaService } from "src/service/media.service";
import { NetworkService } from "src/service/network.service";
import { ReporthtmlService } from "src/service/reporthtml.service";

@Component({
    selector: "app-accounting-detail",
    templateUrl: "./accounting-detail.component.html",
    styleUrls: ["./accounting-detail.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AccountingDetailComponent implements OnInit, OnDestroy, OnChanges, iEvent, iGenericTable {
  @Input("idsAccountingNotes") idsAccountingNotes: string = null;
  @Input("showConfirmButton") showConfirmButton: boolean = true;

  private _logContext: string = "Load-control-list.component";
  private _networkServiceSubscription: Subscription = null;

  //#region Translation
  private _addressName: string = "addressName";
  private _amount: string = "amount";
  private _discount1: string = "discount1";
  private _discount2: string = "discount2";
  private _discount3: string = "discount3";
  private _discount4: string = "discount4";
  private _percentage: string = "percentage";
  //#endregion

  private _auto: string = "auto";

  private _listNotes: AccountingNote[] = [];

  public _eventSubscriptions: Array<Subscription> = null;

  public toolBarColor: string = "primary";
  public currentConnectionType: ConnectionType = ConnectionType.NONE;

  public showDetail: boolean = false;
  public tableInfo: TableInfo = null;
  public tableInfoSecond: TableInfo = null;
  public selected: Array<any> = new Array<any>();

  constructor(
    private _networkService: NetworkService,
    private _translateService: TranslateService,
    private _modalController: ModalController,
    private _accountingService: AccountingService,
    private _reportHTMLService: ReporthtmlService,
    private _popoverCtrl: PopoverController,
    private _eventService: EventService,
    private _loggerService: LoggerService,
    private _alertService: AlertService,
    private _mediaService: MediaService
  ) {
    _accountingService.Init();
  }

  public ngOnInit(): void {
    this.tableInfo = StaticClass.createTableInfo("material expandable", false, null, this.createArrayColumns(), false, false);
    this.tableInfo.IsListInitialized = false;
    this.tableInfo.RowHeight = this._auto;
    this.tableInfoSecond = StaticClass.createTableInfo("", false, null, this.createArrayColumnsSecondTable(), false, false);
    this.tableInfoSecond.RowHeight = this._auto;
    this.subscribeNetworkServiceData();
    this.ngOnChanges();
  }

  public ngOnDestroy(): void {
    this._networkServiceSubscription.unsubscribe();
  }

  /**
   * Load the accounting notes from server and their details information
   */
  public ngOnChanges(): void {
    if (this.idsAccountingNotes && this.idsAccountingNotes != null) {
      this._accountingService
        .GetAccountingNotes(this.idsAccountingNotes, "")
        .then((notesList) => {
          this._listNotes = notesList;
          this.createItemHistoryDesktopList();
          this.tableInfo.IsListInitialized = true;
        })
        .catch((error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            this._logContext,
            "ngOnChanges()",
            "ERROR: " + error.message
          );
          this.handlePeculiarApiResponseError(error);
        });
    }
  }

  //#region Subscribe to event-service observable
  public subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnMakeForceLogout.subscribe(() => {
        this._modalController.dismiss();
      })
    );
  }

  public unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  /**
   * Creates the array containing the objects to show in the app view.
   */
  public createItemHistoryList(): void {
  }

  /**
   * Creates and returns the columns to pass to the table.
   */
  public createArrayColumns(): Column[] {
    let columns: Column[] = [];
    columns.push(StaticClass.createColumnObj("descriptionEntity", "description", "", ColumnTableType.STRING, HideShowColumn.SHOW));
    columns.push(StaticClass.createColumnObj("startDate", "start_date", "", ColumnTableType.STRING, HideShowColumn.SHOW)); //Vedere se metterla come stringa o come data
    columns.push(StaticClass.createColumnObj("endDate", "end_date", "", ColumnTableType.STRING, HideShowColumn.SHOW));
    columns.push(StaticClass.createColumnObj(this._amount, this._amount, "", ColumnTableType.ROUND_NUMBER_2, HideShowColumn.SHOW));
    return columns;
  }

  /**
   * Creates and returns the columns to pass to the second table.
   */
  public createArrayColumnsSecondTable(): Column[] {
    let columns: Column[] = [];
    columns.push(StaticClass.createColumnObj("paymentEntityName", "counterName", "", ColumnTableType.STRING, HideShowColumn.SHOW));
    columns.push(StaticClass.createColumnObj(this._addressName, this._addressName, "", ColumnTableType.STRING, HideShowColumn.SHOW_DETAILS));
    columns.push(StaticClass.createColumnObj("childrenValue", "childCountersValue", "", ColumnTableType.ROUND_NUMBER_5, HideShowColumn.SHOW_DETAILS));
    columns.push(StaticClass.createColumnObj("paymentProfileDescription", "accountingProfile", "", ColumnTableType.STRING, HideShowColumn.SHOW));
    columns.push(StaticClass.createColumnObj("startValue", "firstValue", "", ColumnTableType.ROUND_NUMBER_5, HideShowColumn.SHOW));
    columns.push(StaticClass.createColumnObj("startValueDate", "dateFirstValue", "", ColumnTableType.STRING, HideShowColumn.SHOW));
    columns.push(StaticClass.createColumnObj("endValue", "lastValue", "", ColumnTableType.ROUND_NUMBER_5, HideShowColumn.SHOW));
    columns.push(StaticClass.createColumnObj("endValueDate", "dateLastValue", "", ColumnTableType.STRING, HideShowColumn.SHOW));
    columns.push(StaticClass.createColumnObj("value", "difference", "", ColumnTableType.ROUND_NUMBER_5, HideShowColumn.SHOW));
    columns.push(StaticClass.createColumnObj("thousand", "thousandts", "", ColumnTableType.STRING, HideShowColumn.SHOW_DETAILS));
    columns.push(StaticClass.createColumnObj(this._percentage, this._percentage, "", ColumnTableType.STRING, HideShowColumn.SHOW_DETAILS));
    columns.push(StaticClass.createColumnObj("unityprice", "unitPrice", "", ColumnTableType.STRING, HideShowColumn.SHOW_DETAILS));
    columns.push(StaticClass.createColumnObj(this._discount1, this._discount1, "", ColumnTableType.ROUND_NUMBER_2, HideShowColumn.SHOW_DETAILS));
    columns.push(StaticClass.createColumnObj(this._discount2, this._discount2, "", ColumnTableType.ROUND_NUMBER_2, HideShowColumn.SHOW_DETAILS));
    columns.push(StaticClass.createColumnObj(this._discount3, this._discount3, "", ColumnTableType.ROUND_NUMBER_2, HideShowColumn.SHOW_DETAILS));
    columns.push(StaticClass.createColumnObj(this._discount4, this._discount4, "", ColumnTableType.ROUND_NUMBER_2, HideShowColumn.SHOW_DETAILS));
    columns.push(StaticClass.createColumnObj(this._amount, this._amount, "", ColumnTableType.ROUND_NUMBER_2, HideShowColumn.SHOW));
    return columns;
  }

  /**
   * Creates the table rows.
   */
  public createItemHistoryDesktopList(): void {
    this.tableInfo.List = [];
    this.tableInfo.List = StaticClass.createArrayForTable(this._listNotes, []);
  }

  /**
   * Closes the modal.
   */
  public onClickCancel(): void {
    this._modalController.dismiss();
  }

  /**
   * Generate the accounting data and eventually the export file
   */
  public async GenerateAccountingPDF(): Promise<void> {
    this._reportHTMLService.Init();
    const subs = this._reportHTMLService.reportHTMLlist.subscribe(
      async (reportList) => {
        let buttons = [];
        for (let i = 0; i < reportList.length; i++) {
          buttons[i] = {
            name: reportList[i].description,
            value: reportList[i].id
          };
        }

        let popover = await this._modalController.create({
          component: PopupSelectOperationComponent,
          componentProps: {
            Title: this._translateService.instant("dictionary.templatehtml"),
            Buttons: buttons,
            onclose: () => {
              popover.dismiss();
            },
          },
          cssClass: "modal-operation",
          animated: true,
          showBackdrop: true,
        });

        popover.present();

        const { data } = await popover.onDidDismiss();

        if (data && data != false) {
          let requestObject: AccountingRequestFile = {
            idNotesGenerated: this.idsAccountingNotes,
            idReportHTML: parseInt(data.toString()),
            kindReportHTMPPDF: 0, //pdf
          };

          this._accountingService
            .GeneratePaymentFile(requestObject)
            .then(async (pathOfFileExported) => {
              this._mediaService.openServerFile(
                environment.httpServerRoot +
                environment.apiFolderDownloads +
                pathOfFileExported.path
              );
            })
            .catch((error) => {
              this._loggerService.log(
                LogLevel.LOG_ERROR,
                this._logContext,
                this._logContext,
                "GenerateAccountingPDF()",
                "ERROR: " + error.message
              );
              this.handlePeculiarApiResponseError(error);
            });
        }

        subs.unsubscribe();
      }
    );
  }

  /**
   * Generate the accounting data and eventually the export file
   */
  public GenerateAccountingExcel(): void {
    let requestObject: AccountingRequestFile = {
      idNotesGenerated: this.idsAccountingNotes,
      idReportHTML: 0,
      kindReportHTMPPDF: 1, //excel
    };

    this._accountingService
      .GeneratePaymentFile(requestObject)
      .then((pathOfFileExported) => {
        this._mediaService.openServerFile(
          environment.httpServerRoot +
          environment.apiFolderDownloads +
          pathOfFileExported.path
        );
      })
      .catch((error) => {
        this._loggerService.log(
          LogLevel.LOG_ERROR,
          this._logContext,
          this._logContext,
          "GenerateAccountingExcel()",
          "ERROR: " + error.message
        );
        this.handlePeculiarApiResponseError(error);
      });
  }

  /**
   * Generate the accounting data and eventually the export file
   */
  public GenerateAccountingExcelCityUp(): void {
    let requestObject: AccountingRequestFile = {
      idNotesGenerated: this.idsAccountingNotes,
      idReportHTML: 0,
      kindReportHTMPPDF: 3, //cityUp
    };

    this._accountingService
      .GeneratePaymentFile(requestObject)
      .then((pathOfFileExported) => {
        this._mediaService.openServerFile(
          environment.httpServerRoot +
          environment.apiFolderDownloads +
          pathOfFileExported.path
        );
      })
      .catch((error) => {
        this._loggerService.log(
          LogLevel.LOG_ERROR,
          this._logContext,
          this._logContext,
          "GenerateAccountingExcelCityUp()",
          "ERROR: " + error.message
        );
        this.handlePeculiarApiResponseError(error);
      });
  }

  /**
   * Save accounting note in order to retrieve it later
   */
  public SaveNote(): void {
    this._accountingService
      .SaveAccountingNotes(this.idsAccountingNotes)
      .then(() => this._modalController.dismiss())
      .catch((error) => {
        this._loggerService.log(
          LogLevel.LOG_ERROR,
          this._logContext,
          this._logContext,
          "SaveNote()",
          "ERROR: " + error.message
        );
        this.handlePeculiarApiResponseError(error);
      });
  }

  /**
   * Show options
   * @param myEvent
   */
  public async presentPopover(myEvent: any): Promise<void> {
    let popover = await this._popoverCtrl.create({
      component: PopoverAnagraphActionsComponent,
      componentProps: {
        ShowPDFExport: true,
        ShowExcelExport: true,
        ShowExcelCityUPExport: true,
        ViewDetail: !this.showDetail,
        HideDetail: this.showDetail,
        Save: this.showConfirmButton,
        onClose: () => {
          popover.dismiss();
        },
      },
      event: myEvent,
      animated: true,
      showBackdrop: true,
    });

    popover.present();
    const { data } = await popover.onDidDismiss();
    if (data) {
      switch (data) {
        case "pdfexport":
          this.GenerateAccountingPDF();
          break;
        case "viewDetail":
        case "hideDetail":
          this.showDetail = !this.showDetail;
          break;
        case "excelexport":
          this.GenerateAccountingExcel();
          break;
        case "excelcityupexport":
          this.GenerateAccountingExcelCityUp();
          break;
        case "save":
          this.SaveNote();
          break;
        default:
          break;
      }
    }
  }

  /**
   * Subscribe to the public Observable of "NetworkService", which emits each time the connection change of type/state. A local property is changed to
   * reflect the new value.
   */
  private subscribeNetworkServiceData(): void {
    this._networkServiceSubscription =
      this._networkService.currentConnectionType.subscribe(
        (currentConnectionType: ConnectionType) => {
          this.currentConnectionType = currentConnectionType;
          this.toolBarColor = StaticClass.getComponentColorByNetworkState(
            this.currentConnectionType
          );
        }
      );
  }

  /**
   * Handle errors in api call which require peculiar reactions from page;
   * this includes cases where the error is propagated through subject.error()
   */
  private handlePeculiarApiResponseError(error: any): void {
    switch (error.status) {
      case ApiErrorType.NOT_FOUND:
        this.tableInfo.IsFunctionalityNotImplemented = true;
        break;
      default:
        this.handleApiResponseError();
        break;
    }
  }

  private async handleApiResponseError(): Promise<void> {
    await this._alertService.presentAlertSomethingWrong();
  }
}
